import React, { useRef, useState } from 'react';
import { Col, Layout, message, Skeleton } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../../../constants/urls/api-urls';
import { PRODUCT } from '../../../constants/define/translation';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import { ProductPageHeader } from '../../organisms/product/ProductPageHeader';
import { ProductTable } from '../../organisms/product/ProductTable';
import {
  getProductFromEcomRepository,
  getProductOrdersRepository,
} from '../../../repository/mytm/shop-product/shopProductRepository';
import { FormProductModal } from '../../organisms/product/forms/FormProductModal';
import { PER_PAGE_20 } from '../../../constants/common';
import SelectButtons from '../../atoms/select-buttons/SelectButtons';
import { useCountrySelection } from '../../../hooks/useCountrySelection';

const { Content } = Layout;

export const Product = () => {
  const { t } = useTranslation(PRODUCT);
  const [loading, setLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState<Boolean>(false);
  const [dataRecord, setDataRecord] = useState(null);
  const [countrySelected, setCountrySelected] = useCountrySelection(null);

  const updateProductFromEcom = async () => {
    if (!loading && countrySelected) {
      setLoading(true);
      await getProductFromEcomRepository({ country: countrySelected })
        .then(r => {
          mutate();
          setLoading(false);
          message.success(t('message.updateProductEcomSuccess'));
        })
        .catch(e => {
          setLoading(false);
          message.error(e?.errorMessage || t('message.updateProductEcomError'));
        });
    }
  };
  const errorCount = useRef(0);
  const { data, isValidating, error, mutate } = useSWR(
    countrySelected ? [API_URLS.PRODUCT_ORDERS, countrySelected] : null,
    () => getProductOrdersRepository({ country: countrySelected! }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  if (!data || error) {
    return (
      <Container>
        <ProductPageHeader onGetEcomProduct={updateProductFromEcom} extraDropdown={extraDropdown} />
        <Content>
          <Skeleton active />
        </Content>
      </Container>
    );
  }

  const recordProductOrders = data.map((item: any, index: number) => ({
    ...item,
    ...{ index },
  }));

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  const onCloseModal = () => {
    setIsOpenModal(false);
  };

  return (
    <Container>
      <ProductPageHeader onGetEcomProduct={updateProductFromEcom} />
      <SelectButtons onChangeValue={(item: string) => setCountrySelected(item)} />
      <Content>
        <Col span={14}>
          <ProductTable
            setIsOpenModal={value => setIsOpenModal(value)}
            records={recordProductOrders}
            setDataRecord={setDataRecord}
            loading={loading || isValidating}
            onRefresh={mutate}
            pagination={{
              pageSize: PER_PAGE_20,
            }}
            countrySelected={countrySelected || 'USA'}
          />
        </Col>
      </Content>
      {isOpenModal && (
        <FormProductModal setDataRecord={setDataRecord} onRefresh={mutate} record={dataRecord} onClose={onCloseModal} />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}
  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }
  .draft-tourtrash-table {
    .ant-table-thead {
      display: none;
    }
  }
  .ant-spin-nested-loading > div > .ant-spin {
    background: rgba(255, 255, 255, 0.7);
    max-height: 100%;
  }
  .ant-pagination-item a {
    line-height: 30px;
  }
`;
